import { ApiErrorCode, apiErrorCode } from "utils/errors";

/** Why a call booking didn't go through, as the scheduler explains it. */
export type CallBookingFailure =
  | { kind: "slot_taken" }
  | { kind: "error" }
  | { kind: "email_undeliverable"; email: string | null };

type ResponseBody = { email?: unknown } | null | undefined;

/**
 * A refusal the API names by `error_code` that the scheduler explains in
 * place without re-checking the slot. Adding one is an entry here, its member
 * of `CallBookingFailure`, and its line in the copy map below.
 */
const NAMED_REFUSALS: Record<
  string,
  (body: ResponseBody) => CallBookingFailure
> = {
  [ApiErrorCode.PreNeed.ATTENDEE_EMAIL_UNDELIVERABLE]: (body) => ({
    kind: "email_undeliverable",
    email:
      typeof body?.email === "string" && body.email.trim() !== ""
        ? body.email
        : null,
  }),
};

/**
 * The failure for a booking error the API named with a registered
 * `error_code`, or null for every other error. Structural, not
 * `instanceof NetworkError`: that module pulls in the app config and
 * Firebase, which the node-env unit tests can't load.
 */
export function namedBookingFailure(error: unknown): CallBookingFailure | null {
  const code = apiErrorCode(error);
  if (code === undefined) return null;
  if (!Object.prototype.hasOwnProperty.call(NAMED_REFUSALS, code)) return null;
  const { responseData } = error as { responseData?: ResponseBody };
  return NAMED_REFUSALS[code](responseData);
}

const NOTICES: {
  [K in CallBookingFailure["kind"]]: {
    /** What shows under the picker; null when another element tells it. */
    message: string | null;
  };
} = {
  slot_taken: {
    message: "Sorry, that time is no longer available. Pick another time.",
  },
  error: {
    message:
      "We couldn't book that time. Give it another try, or give us a call.",
  },
  email_undeliverable: { message: null },
};

/** The notice a failure shows under the picker, or null when none does
 * (or there is no failure). */
export function bookingNotice(
  failure: CallBookingFailure | null,
): string | null {
  if (failure === null) return null;
  return NOTICES[failure.kind].message;
}
